import { PriorityHigh } from '@mui/icons-material'
import { Box, Paper, Stack } from '@mui/material'
import axios from 'axios'
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import { useAlertContext } from '@/contexts/AlertContext'
import { ConfigReviewEditsDialog } from '@/features/controller/configuration/components/ConfigReviewEditsDialog'
import { ConfigVersionInfo } from '@/features/controller/configuration/components/ConfigVersionInfo'
import { ConfigVersionNavigationLinks } from '@/features/controller/configuration/components/ConfigVersionNavigationLinks'
import { TextEditor } from '@/features/controller/configuration/components/textEditor/TextEditor'
import { useControllerConfigurationFileContext } from '@/features/controller/configuration/contexts/ControllerConfigurationFileContext'
import type { ControllerConfigVersion, ControllerConfigVersionUpdate } from '@/features/controller/configuration/types'
import { getControllerConfigType } from '@/features/controller/configuration/utils/controllerConfigTypes'
import { validateConfigText } from '@/features/controller/configuration/utils/validateConfigText'
import { useControllerRestartStatusPolling } from '@/features/controller/restart/hooks/useControllerRestartStatusPolling'
import { useRestartControllerMutation } from '@/features/controller/restart/hooks/useRestartControllerMutation'
import { ControllerRestartMethod } from '@/features/controller/restart/types'
import CancelConfirmIconButtons from '@/features/site/components/CancelConfirmIconButtons'
import EditIconButton from '@/features/site/components/EditIconButton'

interface ConfigVersionDisplayEditableProps {
  configVersion: ControllerConfigVersion
  controllerId: string
  infoPanel: ReactNode
  schema?: object | null
}

interface ControllerConfigurationEditingState {
  proposedEdit?: ControllerConfigVersionUpdate
}

/**
 * Reusable component for displaying a config version with editing capability
 */
export function ConfigVersionDisplayEditable({
  configVersion,
  controllerId,
  infoPanel,
  schema,
}: Readonly<ConfigVersionDisplayEditableProps>) {
  const { pushAlert } = useAlertContext()
  const { t } = useTranslation()
  const { state } = useLocation()

  const { updateAndInvalidate, isUpdatePending } = useControllerConfigurationFileContext()

  const [editMode, setEditMode] = useState(false)
  const [reviewMode, setReviewMode] = useState(false)
  const [text, setText] = useState<string | undefined>(configVersion.value)
  const [themeRefresh, setThemeRefresh] = useState(0)
  const [isSchemaValid, setIsSchemaValid] = useState(true)
  const [hasProposedEdit, setHasProposedEdit] = useState<boolean>()
  const [proposedComment, setProposedComment] = useState<string | undefined>(undefined)
  const hasProcessedProposedEdit = useRef(false)

  const configType = getControllerConfigType(configVersion.configurationTypeId)

  useEffect(() => {
    const pe = (state as ControllerConfigurationEditingState)?.proposedEdit
    if (pe && !hasProcessedProposedEdit.current) {
      hasProcessedProposedEdit.current = true
      if (pe.previousVersionId !== configVersion.id) {
        pushAlert({
          message: t('controllers.configuration.proposed_edit.proposal_error_conflict'),
          severity: 'error',
        })
      } else {
        setText(pe.value)
        setProposedComment(pe.comment)
        setHasProposedEdit(true)
        setEditMode(true)
      }
      globalThis.history.replaceState({}, '')
    }
  }, [configVersion])

  function enterEditMode() {
    setText(configVersion.value)
    setEditMode(true)
  }

  function cancelEditMode() {
    setText(configVersion.value)
    setHasProposedEdit(false)
    setEditMode(false)
  }

  function enterReviewMode() {
    // Sync validation on submit
    const { isValid, errorMessage } = validateConfigText(configType, currentText, t)
    if (!isValid) {
      // Don't open review dialog and push alert message to user
      pushAlert({
        message: errorMessage!,
        severity: 'error',
      })
      return
    }

    setReviewMode(true)
  }

  function exitReviewMode() {
    setReviewMode(false)
    setThemeRefresh(Date.now())
  }

  const { restartController, isPending: isRestartingController } = useRestartControllerMutation(
    controllerId,
    ControllerRestartMethod.SSH,
  )

  // Enable polling when restart is in progress
  useControllerRestartStatusPolling({
    controllerId,
    enabled: true,
  })

  async function onSave(update: ControllerConfigVersionUpdate) {
    try {
      setText(update.value)
      await updateAndInvalidate(update)

      pushAlert({
        message: t('controllers.configuration.update_success'),
        severity: 'success',
      })
      exitReviewMode()
      setHasProposedEdit(false)
      setEditMode(false)
      return true
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        pushAlert({
          message: t('controllers.configuration.update_error_conflict'),
          severity: 'error',
        })
      } else {
        pushAlert({
          message: t('controllers.configuration.update_error'),
          severity: 'error',
        })
      }
      return false
    }
  }

  async function onSaveAndRestart(update: ControllerConfigVersionUpdate) {
    const saveSucceeded = await onSave(update)
    if (saveSucceeded) {
      try {
        await restartController()
        // Don't show success immediately - the polling mechanism will handle status updates
        // The restart request was accepted, but actual restart success will be shown via RestartProgressSection
        pushAlert({
          message: t('controllers.configuration.restart_requested'),
          severity: 'info',
        })
      } catch {
        pushAlert({
          message: t('controllers.configuration.restart_error'),
          severity: 'error',
        })
        return false
      }
    }
  }

  // The editor does not accept undefined text but can provide undefined in OnChange
  const currentText = text || ''

  return (
    <Box my={2}>
      <Stack direction="row" gap={2}>
        {/* Main editor section */}
        <Box sx={{ flex: 3 }}>
          <Paper sx={{ p: 3 }}>
            {/* Optional title */}
            <CustomTypography sx={{ mb: 2 }} variant="h2">
              {configType.name}
            </CustomTypography>

            {/* Proposed edit info */}
            {hasProposedEdit && (
              <Stack direction="row" sx={{ mt: 2 }}>
                <PriorityHigh />
                <CustomTypography>{t('controllers.configuration.proposed_edit.proposal_info')}</CustomTypography>
              </Stack>
            )}

            {/* Edit controls */}
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
              {editMode ? (
                <CancelConfirmIconButtons
                  cancelDisabled={false}
                  disabled={!isSchemaValid}
                  onCancelClick={cancelEditMode}
                  onConfimClick={enterReviewMode}
                />
              ) : (
                <EditIconButton tooltipText="Edit configuration" onClick={enterEditMode} />
              )}
            </Stack>

            {/* Text Editor */}
            <TextEditor
              configTypeId={configType.id}
              language={configType.textEditorLanguage}
              refreshTrigger={themeRefresh}
              schema={schema}
              value={currentText}
              onChange={editMode ? setText : undefined}
              onValidationChange={setIsSchemaValid}
            />

            {/* Review dialog (only for editable mode) */}
            <ConfigReviewEditsDialog
              isUpdating={isUpdatePending || isRestartingController}
              language={configType.textEditorLanguage}
              modifiedText={currentText}
              open={reviewMode}
              originalText={configVersion.value}
              originalVersionId={configVersion.id}
              proposedComment={proposedComment}
              onClose={exitReviewMode}
              onSave={onSave}
              onSaveAndRestart={onSaveAndRestart}
            />
          </Paper>
        </Box>

        {/* Version info section */}
        <Box sx={{ flex: 2 }}>
          {infoPanel}
          <ConfigVersionInfo configVersion={configVersion} controllerId={controllerId} />
          {configVersion.previousVersionId && (
            <ConfigVersionNavigationLinks
              configVersionId={configVersion.id}
              controllerId={controllerId}
              previousVersionId={configVersion.previousVersionId}
            />
          )}
        </Box>
      </Stack>
    </Box>
  )
}
